import { type ComponentProps, type ReactNode, useEffect, useRef } from 'react';

import { css, keyframes } from '@compiled/react';

import { IconButton } from '@atlaskit/button-v25/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { Text } from '@atlaskit/primitives-next';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { EASE_OUT } from './jsm-playground-motion';
import { JsmPlaygroundRovoMorphingShape } from './jsm-playground-rovo-morphing-shape';

type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const CloseIcon: IconButtonIcon = () => <CrossIcon label="" size="medium" />;
const HelpfulIcon: IconButtonIcon = () => <ThumbsUpIcon label="" size="small" />;
const NotHelpfulIcon: IconButtonIcon = () => <ThumbsDownIcon label="" size="small" />;

const noop = () => {};

/** Shorter than a real answer on purpose — they only need to read as "loading". */
const thinkingLineWidths = ['42%', '100%', '100%', '64%'];

export type JsmPlaygroundRovoCardProps = {
	id?: string;
	title: string;
	closeLabel: string;
	onClose: () => void;
	isThinking: boolean;
	thinkingLabel: string;
	/**
	 * The whole answer as plain text, announced once when it lands. The visible answer may be typed in
	 * character by character, which must not be read out as it types.
	 */
	announcement: string;
	footerNote: string;
	helpfulLabel: string;
	notHelpfulLabel: string;
	/** The composed answer, shown once thinking is done. */
	children: ReactNode;
	/** Off when a scripted demo opened it: a scripted open must not move focus or scroll the page. */
	shouldFocusOnOpen?: boolean;
};

/**
 * A Rovo answer surface: a titled card that opens on a "thinking" state, then swaps in the answer
 * with a short fade-and-rise so the resolve reads as a reveal rather than a layout jump.
 *
 * It is a non-modal dialog. Focus moves to its close button when it opens; the caller owns
 * positioning, Escape, and returning focus to whatever opened it.
 */
export const JsmPlaygroundRovoCard = ({
	id,
	title,
	closeLabel,
	onClose,
	isThinking,
	thinkingLabel,
	announcement,
	footerNote,
	helpfulLabel,
	notHelpfulLabel,
	children,
	shouldFocusOnOpen = true,
}: JsmPlaygroundRovoCardProps) => {
	const titleId = `${id ?? 'jsm-playground-rovo-card'}-title`;
	const closeRef = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		if (shouldFocusOnOpen) {
			closeRef.current?.querySelector('button')?.focus();
		}
		// Only on open: the card should not grab focus back if the prop changes later.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div id={id} css={cardStyles} role="dialog" aria-labelledby={titleId}>
			<div css={bodyStyles}>
				<div css={headerStyles}>
					<Heading size="xsmall" as="h5" id={titleId}>
						{title}
					</Heading>
					<span ref={closeRef}>
						<IconButton
							appearance="subtle"
							icon={CloseIcon}
							label={closeLabel}
							onClick={onClose}
							spacing="compact"
						/>
					</span>
				</div>

				<div key={isThinking ? 'thinking' : 'result'} css={phaseStyles}>
					{isThinking ? (
						<div css={thinkingLinesStyles} aria-hidden="true">
							{thinkingLineWidths.map((width, index) => (
								// Identical placeholders, so the index is the only meaningful key.
								// eslint-disable-next-line react/no-array-index-key
								<Skeleton
									key={index}
									borderRadius={token('radius.xsmall')}
									height="16px"
									width={width}
								/>
							))}
						</div>
					) : (
						children
					)}
				</div>
			</div>

			<div css={footerStyles}>
				{isThinking ? (
					<span css={footerGroupStyles}>
						<JsmPlaygroundRovoMorphingShape />
						<Text size="small" color="color.text.subtlest">
							{thinkingLabel}
						</Text>
					</span>
				) : (
					<>
						<span css={footerGroupStyles}>
							<InformationCircleIcon color={token('color.icon.subtle')} label="" size="small" />
							<Text size="small" color="color.text.subtlest">
								{footerNote}
							</Text>
						</span>
						{/* Feedback isn't collected in the preview, so the controls are drawn but inert. */}
						<span
							css={footerGroupStyles}
							aria-hidden="true"
							// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
							inert=""
						>
							<IconButton
								appearance="subtle"
								icon={NotHelpfulIcon}
								label={notHelpfulLabel}
								onClick={noop}
								spacing="compact"
								tabIndex={-1}
							/>
							<IconButton
								appearance="subtle"
								icon={HelpfulIcon}
								label={helpfulLabel}
								onClick={noop}
								spacing="compact"
								tabIndex={-1}
							/>
						</span>
					</>
				)}
			</div>

			<VisuallyHidden>
				<span aria-live="polite">{isThinking ? '' : announcement}</span>
			</VisuallyHidden>
		</div>
	);
};

const cardIn = keyframes({
	from: { opacity: 0, transform: 'translateY(-6px) scale(0.98)' },
	to: { opacity: 1, transform: 'none' },
});

const resultIn = keyframes({
	from: { opacity: 0, transform: 'translateY(6px)' },
	to: { opacity: 1, transform: 'none' },
});

const cardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	boxSizing: 'border-box',
	width: '480px',
	border: `${token('border.width', '1px')} solid ${token('color.border')}`,
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface.overlay'),
	boxShadow: token('elevation.shadow.overlay'),
	overflow: 'hidden',
	animation: `${cardIn} 260ms cubic-bezier(0.15, 1, 0.3, 1)`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const bodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
	padding: token('space.200'),
});

const headerStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
});

const phaseStyles = css({
	animation: `${resultIn} 320ms ${EASE_OUT}`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const thinkingLinesStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
});

const footerStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
	minHeight: token('space.400'),
	paddingBlockEnd: token('space.150'),
	paddingInline: token('space.200'),
});

const footerGroupStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.075'),
});
